// src/pages/Blog/Blog.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogAPI } from '../../api/blog.js';
import { Breadcrumbs } from '../../components/common/Breadcrumbs/Breadcrumbs';
import { Loader } from '../../components/ui/Loader/Loader.jsx';
import { Button } from '../../components/ui/Button/Button.jsx';
import { formatDate } from '../../utils/formatters.js';

export const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadPosts();
  }, [filter]);

  const loadPosts = async () => {
    setIsLoading(true);
    try {
      const params = filter !== 'all' ? { category: filter } : {};
      const response = await blogAPI.getAll(params);
      setPosts(response.data.data || []);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = [
    { value: 'all', label: 'Все' },
    { value: 'guides', label: 'Гайды' },
    { value: 'cases', label: 'Кейсы' },
    { value: 'news', label: 'Новости' },
    { value: 'tutorials', label: 'Обучение' },
  ];

  const breadcrumbs = [{ label: 'Блог', path: '/blog' }];

  return (
    <div style={{ backgroundColor: 'rgb(var(--c-bg))', minHeight: '100vh', paddingBottom: '40px' }}>
      <div className="container" style={{ paddingTop: '24px' }}>
        <Breadcrumbs items={breadcrumbs} />

        <h1 style={{
          fontSize: '32px',
          fontWeight: 700,
          color: 'rgb(var(--c-text))',
          marginTop: '24px',
          marginBottom: '8px',
        }}>
          Блог
        </h1>
        <p style={{
          fontSize: '16px',
          fontWeight: 300,
          color: 'rgb(var(--c-text-secondary))',
          marginBottom: '32px',
        }}>
          Полезные статьи и новости
        </p>

        {/* Фильтры */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '32px',
          flexWrap: 'wrap',
        }}>
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setFilter(cat.value)}
              style={{
                padding: '8px 20px',
                borderRadius: '100px',
                border: filter === cat.value ? '1px solid rgb(var(--c-accent))' : '1px solid rgb(var(--c-border))',
                backgroundColor: filter === cat.value ? '#205CA0' : 'transparent',
                color: filter === cat.value ? '#FAFAFA' : '#1A1A1A',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 0.2s',
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <Loader text="Загрузка статей..." />
        ) : posts.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 0',
            color: 'rgb(var(--c-text-secondary))',
          }}>
            <p>Статей не найдено</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px',
          }}>
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div style={{
                  backgroundColor: 'rgb(var(--c-surface))',
                  border: '1px solid rgb(var(--c-border))',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.08)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{
                    backgroundColor: 'rgb(var(--c-surface-2))',
                    height: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'rgb(var(--c-text-secondary))',
                    fontSize: '12px',
                  }}>
                    {post.cover_image ? 'Изображение' : 'Нет фото'}
                  </div>
                  <div style={{ padding: '20px' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: 'rgb(var(--c-accent))',
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                      }}>
                        {post.category}
                      </span>
                      <span style={{
                        fontSize: '12px',
                        color: 'rgb(var(--c-text-secondary))',
                      }}>
                        {formatDate(post.published_at || post.createdAt)}
                      </span>
                    </div>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: 'rgb(var(--c-text))',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                      {post.title}
                    </h3>
                    {post.excerpt && (
                      <p style={{
                        fontSize: '14px',
                        fontWeight: 300,
                        color: 'rgb(var(--c-text-secondary))',
                        marginTop: '8px',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {post.excerpt}
                      </p>
                    )}
                    <div style={{
                      marginTop: '16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <span style={{
                        fontSize: '13px',
                        color: 'rgb(var(--c-text-secondary))',
                      }}>
                        👁 {post.view_count || 0}
                      </span>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'rgb(var(--c-accent))',
                      }}>
                        Читать →
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};